import { Box, Button, LinearProgress, IconButton, TextField } from '@material-ui/core';
import {
  AddCircleOutline,
  CheckCircleOutline,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import {
  GivingAccount,
  useGivingAccountSummaryQuery,
  useGivingAccountTransactionsQuery,
  useUpdateGivingAccountMutation,
} from 'generated/graphql';
import { selectUser } from 'entities/user/selectors';
import BackButtonLayoutTitleAction from 'retired/BackButtonLayout/BackButtonLayoutTitleAction';
import MonthRangePicker from 'retired/MonthRangePicker';
import { CurrencyCode } from 'utils/currencyFormat';
import { useInjectReducer } from 'utils/injectReducer';
import { Privileges } from 'utils/privileges';
import { CreateGivingAccountModal } from 'containers/GivingAccountsPage/CreateGivingAccountModal';
import { showErrorAction } from 'containers/Notification/actions';

import { setFilterAction } from './actions';
import { AddGivingAccountFundingModal } from './AddGivingAccountFundingModal';
import { UpdateProcessedPaymentModal } from './UpdateProcessedPaymentModal';
import { key } from './constants';
import Counter from './Counter';
import GivingAccountBanner from './GivingAccountBanner';
import { summarizeTransactions } from './summarizeTransactions';
import reducer from './reducers';
import { selectDateFrom, selectDateTo } from './selectors';
import StatsSection from './StatsSection';
import { PendingTransactionsTable } from './PendingTransactionsTable';
import { ProcessedTransactionsTable } from './ProcessedTransactionsTable';
import { renderGivingAccountName } from './renderGivingAccountName';

const GivingAccountPage = (): JSX.Element => {
  useInjectReducer({ key, reducer });
  const { id } = useParams<{ id: string }>();

  const history = useHistory();

  const dispatch = useDispatch();

  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [updateGivingAccount] = useUpdateGivingAccountMutation({
    onError: () => {
      dispatch(showErrorAction('Unable to rename the Giving Account'));
    },
  });

  const [addGivingAccountFundingModalOpen, setAddGivingAccountFundingModalOpen] = useState(false);
  const [updateProcessedPaymentModalOpen, setUpdateProcessedPaymentModalOpen] = useState(false);

  const user = useSelector(selectUser);
  const dateFrom = useSelector(selectDateFrom);
  const dateTo = useSelector(selectDateTo);

  const budgetLedgerEnabled = user && user.hasPrivilege(Privileges.BUDGET_LEDGER_VIEW);
  if (user && !budgetLedgerEnabled) {
    history.replace('/');
  }

  const {
    data: accountData,
    loading: accountLoading,
    error: accountError,
  } = useGivingAccountSummaryQuery({
    variables: { id },
    notifyOnNetworkStatusChange: true,
    refetchWritePolicy: 'overwrite',
  });
  const {
    data: transactionsData,
    loading: transactionsLoading,
    error: transactionsError,
  } = useGivingAccountTransactionsQuery({
    variables: {
      id,
      createdAtSince: dateFrom?.toISOString(),
      createdAtTill: dateTo?.toISOString(),
    },
    notifyOnNetworkStatusChange: true,
    refetchWritePolicy: 'overwrite',
  });

  const hasMultipleAccounts = Number(accountData?.givingAccounts.length) > 1;

  const accountCurrency = (accountData?.givingAccount?.currencyCode || 'USD') as CurrencyCode;

  const accountProvider = accountData?.givingAccount?.provider || null;

  const loading = accountLoading || transactionsLoading;
  const error = accountError || transactionsError;

  const transactions = transactionsData?.givingAccount?.transactions || [];

  // @TODO extract this logic
  const firstTransaction = transactions[transactions.length - 1];
  const openingBalance = firstTransaction
    ? (firstTransaction.balance || 0) -
      (firstTransaction.__typename === 'GivingAccountWithdrawal'
        ? -1 * (firstTransaction.totalAccountCurrency || 0)
        : firstTransaction.amount)
    : 0;
  const closingBalance = transactions[0]?.balance || 0;

  const { paidAmount, processedTransactions, pendingTransactions, receivedAmount } =
    summarizeTransactions(transactions);

  const givingAccount: GivingAccount = accountData?.givingAccount;

  const handleCloseAddGivingAccountFundingModal = (): void => {
    setAddGivingAccountFundingModalOpen(false);
  };

  const handleCloseUpdatePaymentProcessingModal = (): void => {
    setUpdateProcessedPaymentModalOpen(false);
  };

  const handleSave = async () => {
    await updateGivingAccount({
      variables: {
        id,
        name: editedName,
      },
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedName(givingAccount?.name || '');
    setIsEditing(false);
  };

  const title = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {isEditing ? (
        <>
          <TextField
            value={editedName}
            onChange={(e) => setEditedName(e.target.value)}
            variant="outlined"
            size="small"
            style={{ backgroundColor: 'white', borderRadius: '4px' }}
            inputProps={{ style: { color: 'black' } }}
          />
          <IconButton onClick={handleSave} size="small">
            <CheckIcon />
          </IconButton>
          <IconButton onClick={handleCancel} size="small">
            <CloseIcon />
          </IconButton>
        </>
      ) : (
        <>
          {renderGivingAccountName(givingAccount)}
          {user?.hasPrivilege(Privileges.BUDGET_LEDGER_MANAGE) && (
            <IconButton
              onClick={() => {
                setEditedName(givingAccount?.name || '');
                setIsEditing(true);
              }}
              size="small"
            >
              <EditIcon />
            </IconButton>
          )}
        </>
      )}
    </div>
  );

  return (
    <>
      {givingAccount ? (
        <AddGivingAccountFundingModal
          open={addGivingAccountFundingModalOpen}
          onClickClose={handleCloseAddGivingAccountFundingModal}
          givingAccount={givingAccount}
        />
      ) : null}
      {givingAccount && user?.hasPrivilege(Privileges.BUDGET_LEDGER_MANAGE) ? (
        <UpdateProcessedPaymentModal
          open={updateProcessedPaymentModalOpen}
          onClickClose={handleCloseUpdatePaymentProcessingModal}
        />
      ) : null}
      <Helmet title="Giving Account Transactions" />
      <BackButtonLayoutTitleAction
        to={hasMultipleAccounts ? '/donations/ledger' : '/donations'}
        title={title}
        rightActions={
          user?.isAdmin && <CreateGivingAccountModal title="Create another Giving Account" />
        }
      >
        <div>
          <GivingAccountBanner givingAccount={givingAccount} />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginTop: 24,
              paddingTop: 16,
              paddingBottom: 16,
            }}
          >
            <div style={{ flexGrow: 1 }}>
              <MonthRangePicker
                value={[dateFrom, dateTo]}
                onChange={(fromDate, toDate): void => {
                  dispatch(setFilterAction(fromDate, toDate));
                }}
              />
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {user?.hasPrivilege(Privileges.BUDGET_LEDGER_MANAGE) ? (
                <>
                  <Button
                    disabled={loading}
                    onClick={() => setAddGivingAccountFundingModalOpen(true)}
                    endIcon={<AddCircleOutline />}
                  >
                    Add funding
                  </Button>
                  &nbsp;
                </>
              ) : null}
              {user?.hasPrivilege(Privileges.BUDGET_LEDGER_MANAGE) ? (
                <>
                  <Button
                    disabled={loading}
                    onClick={() => setUpdateProcessedPaymentModalOpen(true)}
                    endIcon={<CheckCircleOutline />}
                  >
                    Mark As Processed
                  </Button>
                  &nbsp;
                </>
              ) : null}
              <StatsSection>
                <Counter
                  isFirst
                  label="Opening balance"
                  amount={openingBalance}
                  accountCurrency={accountCurrency}
                />
                <Counter
                  label="Closing balance"
                  amount={closingBalance}
                  accountCurrency={accountCurrency}
                />
                <Counter
                  label="Funds received"
                  amount={receivedAmount}
                  accountCurrency={accountCurrency}
                />
                <Counter label="Funds paid" amount={paidAmount} accountCurrency={accountCurrency} />
              </StatsSection>
            </div>
          </div>
          {error && (
            <Alert severity="error">
              We ran into an error!
              <br />
              <br />
              {error?.message}
            </Alert>
          )}
          {loading ? <LinearProgress /> : <div style={{ height: 4 }} />}
          {pendingTransactions.length > 0 ? (
            <Box>
              {pendingTransactions.map((pendingTransactionsAggregate) => (
                <PendingTransactionsTable
                  key={pendingTransactionsAggregate.group}
                  pendingTransactionsAggregate={pendingTransactionsAggregate}
                  accountCurrency={accountCurrency}
                  accountProvider={accountProvider}
                />
              ))}
            </Box>
          ) : null}
          {processedTransactions.length > 0 ? (
            <Box>
              {processedTransactions.map((processedTransactionsAggregate) => (
                <ProcessedTransactionsTable
                  key={processedTransactionsAggregate.group}
                  processedTransactionsAggregate={processedTransactionsAggregate}
                  accountCurrency={accountCurrency}
                  accountProvider={accountProvider}
                />
              ))}
            </Box>
          ) : null}
        </div>
      </BackButtonLayoutTitleAction>
    </>
  );
};

export default GivingAccountPage;
